import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../lib/supabase";

type Status = "idle" | "loading" | "success" | "error" | "invalid";

export default function AdminResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    // Supabase sets the session automatically from the URL hash on recovery links
    supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setSessionReady(true);
      }
    });

    // Also check if there's already a session (sometimes fires before listener)
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setSessionReady(true);
    });
  }, []);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError("La contraseña debe tener al menos 8 caracteres."); return; }
    if (password !== confirm) { setError("Las contraseñas no coinciden."); return; }
    setStatus("loading");
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) { setError(updateError.message); setStatus("error"); return; }
      setStatus("success");
      setTimeout(() => navigate("/admin"), 2500);
    } catch {
      setError("Error inesperado. Inténtalo de nuevo.");
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center px-4">
      <meta name="robots" content="noindex, nofollow" />
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <img
            src="https://static.readdy.ai/image/464d6b2d88c2c0b329c43db37f7a6b21/5e3d82f418168fbdbc49efd95d2ee821.png"
            alt="Auria"
            className="h-10 w-auto"
          />
        </div>

        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <div className="px-8 pt-8 pb-6 border-b border-stone-100">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-lock-password-line text-amber-500 text-base" />
              </div>
              <span className="text-xs font-bold text-amber-600 uppercase tracking-widest">Nueva contraseña</span>
            </div>
            <h1 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Establece tu contraseña
            </h1>
            <p className="text-stone-400 text-sm mt-1">Elige una contraseña segura para el panel</p>
          </div>

          <div className="px-8 py-6">
            {status === "success" ? (
              <div className="text-center py-4 space-y-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-emerald-50 mx-auto">
                  <i className="ri-checkbox-circle-line text-emerald-500 text-2xl" />
                </div>
                <div>
                  <p className="font-bold text-stone-900 text-sm mb-1">¡Contraseña actualizada!</p>
                  <p className="text-stone-500 text-xs">Redirigiendo al panel de login...</p>
                </div>
              </div>
            ) : !sessionReady ? (
              <div className="text-center py-8 space-y-3">
                <div className="w-10 h-10 flex items-center justify-center mx-auto">
                  <i className="ri-loader-4-line animate-spin text-stone-400 text-2xl" />
                </div>
                <p className="text-stone-500 text-sm">Verificando enlace de recuperación...</p>
                <p className="text-stone-400 text-xs">
                  Si este mensaje no desaparece,{" "}
                  <button
                    onClick={() => navigate("/admin")}
                    className="text-amber-600 font-semibold hover:underline cursor-pointer"
                  >
                    vuelve al login
                  </button>{" "}
                  y solicita un nuevo enlace.
                </p>
              </div>
            ) : (
              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">
                    Nueva contraseña <span className="text-stone-400 font-normal normal-case">(mín. 8 caracteres)</span>
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(null); }}
                    placeholder="Escribe tu nueva contraseña"
                    required
                    className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">
                    Confirmar contraseña
                  </label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => { setConfirm(e.target.value); setError(null); }}
                    placeholder="Repite la contraseña"
                    required
                    className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50 transition-all"
                  />
                </div>

                {error && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                    <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5" />
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading" || !password || !confirm}
                  className="w-full py-3 bg-stone-900 hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {status === "loading" ? (
                    <><i className="ri-loader-4-line animate-spin" /><span>Guardando...</span></>
                  ) : (
                    <><i className="ri-save-line" /><span>Guardar nueva contraseña</span></>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        <p className="text-center text-stone-400 text-xs mt-6">
          Auria · Panel privado de administrador
        </p>
      </div>
    </div>
  );
}
